import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { vehiclesApi } from '../api/vehicles.api';
import { driversApi } from '../api/drivers.api';
import { obdApi } from '../api/obd.api';
import { Vehicle, CreateVehicleData, UpdateVehicleData } from '../types/vehicle';
import { User } from '../types/user';
import { AvailableOBDDevice } from '../types/obd';
import { toast } from '../utils/toast';
import { useAuth } from '../hooks/useAuth';
import { OdometerDisplay } from '../components/ui/OdometerDisplay';

interface VehicleFormData {
  plate_number: string;
  brand: string;
  model: string;
  year: string;
  vin: string;
  current_odometer: string;
  odometer_source: string;
  assigned_driver: string;
}

export const VehiclesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin === true || user?.role === 'super_admin';
  const canToggleSmartcar = isSuperAdmin || user?.role === 'company_admin';
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);

  // Device assignment state
  const [isAssignDeviceModalOpen, setIsAssignDeviceModalOpen] = useState(false);
  const [assigningVehicle, setAssigningVehicle] = useState<Vehicle | null>(null);
  const [availableDevices, setAvailableDevices] = useState<AvailableOBDDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState<string | null>(null);
  const [togglingSmartcar, setTogglingSmartcar] = useState<string | null>(null);
  const [isDeletingVehicle, setIsDeletingVehicle] = useState<string | null>(null);
  const [deleteConfirmVehicle, setDeleteConfirmVehicle] = useState<Vehicle | null>(null);
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
  const deviceSearchRef = useRef<HTMLInputElement>(null);
  const deviceCardRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Format full IMEI grouped as "XXX XXX XXX XXX XXX"
  const formatImei = (imei: string) => imei.replace(/(\d{3})(?=\d)/g, '$1 ').trim();

  // Format last-5 suffix as "··· XX XXX"
  const formatImeiSuffix = (imei: string) => `··· ${imei.slice(-5, -3)} ${imei.slice(-3)}`;

  const [formData, setFormData] = useState<VehicleFormData>({
    plate_number: '',
    brand: '',
    model: '',
    year: '',
    vin: '',
    current_odometer: '0',
    odometer_source: 'obd',
    assigned_driver: '',
  });

  const [formErrors, setFormErrors] = useState<Partial<VehicleFormData>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [vehiclesData, driversData] = await Promise.all([
        vehiclesApi.listVehicles(),
        driversApi.listDrivers(),
      ]);
      setVehicles(Array.isArray(vehiclesData) ? vehiclesData : []);
      setDrivers(Array.isArray(driversData) ? driversData : []);
    } catch (error: any) {
      console.error('Failed to load data:', error);
      toast.error(error.response?.data?.message || t('vehicles.failedLoad'));
      setVehicles([]);
      setDrivers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (isEdit: boolean = false): boolean => {
    const errors: Partial<VehicleFormData> = {};

    if (!formData.plate_number.trim()) errors.plate_number = t('vehicles.validation.plateRequired');
    if (!formData.brand.trim()) errors.brand = t('vehicles.validation.brandRequired');
    if (!formData.model.trim()) errors.model = t('vehicles.validation.modelRequired');

    if (formData.year && !/^\d{4}$/.test(formData.year)) {
      errors.year = t('vehicles.validation.yearInvalid');
    }
    if (formData.vin && formData.vin.length !== 17) {
      errors.vin = t('vehicles.validation.vinInvalid');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      const vehicleData: CreateVehicleData = {
        plate_number: formData.plate_number.trim().toUpperCase(),
        brand: formData.brand.trim(),
        model: formData.model.trim(),
        year: formData.year ? parseInt(formData.year) : undefined,
        vin: formData.vin.trim() || undefined,
        current_odometer: parseInt(formData.current_odometer) || 0,
        assigned_driver: formData.assigned_driver || undefined,
      };

      await vehiclesApi.createVehicle(vehicleData);
      toast.success(t('vehicles.addModal.registeredSuccess'));

      setIsAddModalOpen(false);
      setFormData({
        plate_number: '', brand: '', model: '', year: '', vin: '', current_odometer: '0', odometer_source: 'obd', assigned_driver: '',
      });
      setFormErrors({});
      await loadData();
    } catch (error: any) {
      console.error('Failed to register vehicle:', error);
      toast.error(error.response?.data?.message || t('vehicles.addModal.failedRegister'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof VehicleFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      plate_number: vehicle.plate_number,
      brand: vehicle.brand,
      model: vehicle.model,
      year: vehicle.year?.toString() || '',
      vin: vehicle.vin || '',
      current_odometer: vehicle.current_odometer.toString(),
      odometer_source: vehicle.odometer_source || 'obd',
      assigned_driver: getDriverId(vehicle) || '',
    });
    setFormErrors({});
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm(true)) return;
    if (!editingVehicle) return;

    try {
      setIsSubmitting(true);

      const updateData: UpdateVehicleData = {
        plate_number: formData.plate_number.trim().toUpperCase(),
        brand: formData.brand.trim(),
        model: formData.model.trim(),
        year: formData.year ? parseInt(formData.year) : undefined,
        vin: formData.vin.trim() || undefined,
        current_odometer: parseInt(formData.current_odometer) || 0,
        odometer_source: formData.odometer_source || undefined,
        assigned_driver: formData.assigned_driver || null,
        status: editingVehicle.status,
      };

      await vehiclesApi.updateVehicle(editingVehicle._id, updateData);
      toast.success(t('vehicles.editModal.updatedSuccess'));

      setIsEditModalOpen(false);
      setEditingVehicle(null);
      setFormData({
        plate_number: '', brand: '', model: '', year: '', vin: '', current_odometer: '0', odometer_source: 'obd', assigned_driver: '',
      });
      setFormErrors({});
      await loadData();
    } catch (error: any) {
      console.error('Failed to update vehicle:', error);
      toast.error(error.response?.data?.message || t('vehicles.editModal.failedUpdate'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignDriver = async (vehicleId: string, driverId: string) => {
    try {
      await vehiclesApi.assignDriver(vehicleId, driverId || null);
      toast.success(
        driverId ? t('vehicles.driverAssign.assigned') : t('vehicles.driverAssign.unassigned')
      );
      await loadData();
    } catch (error: any) {
      console.error('Failed to assign driver:', error);
      toast.error(error.response?.data?.message || t('vehicles.driverAssign.failed'));
    }
  };

  const openAssignDeviceModal = async (vehicle: Vehicle) => {
    setAssigningVehicle(vehicle);
    setSelectedDeviceId('');
    setDeviceSearchQuery('');
    setIsAssignDeviceModalOpen(true);
    setIsLoadingDevices(true);
    try {
      const vehicleCompanyId = typeof vehicle.company_id === 'object'
        ? (vehicle.company_id as any)?._id
        : vehicle.company_id as string | undefined;
      const devices = await obdApi.getAvailableDevices(vehicleCompanyId);
      setAvailableDevices(Array.isArray(devices) ? devices : []);
    } catch (error: any) {
      console.error('Failed to load available devices:', error);
      toast.error(error.response?.data?.message || t('vehicles.deviceAssign.failedLoadDevices'));
      setAvailableDevices([]);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const handleAssignDevice = async () => {
    if (!assigningVehicle || !selectedDeviceId) return;
    const selectedDevice = availableDevices.find((d) => d._id === selectedDeviceId);
    if (!selectedDevice) return;
    try {
      setIsAssigning(true);
      await obdApi.assignDevice(selectedDevice.device_id, assigningVehicle._id);
      toast.success(t('vehicles.deviceAssign.assigned'));
      setIsAssignDeviceModalOpen(false);
      setAssigningVehicle(null);
      setSelectedDeviceId('');
      await loadData();
    } catch (error: any) {
      console.error('Failed to assign device:', error);
      toast.error(error.response?.data?.message || t('vehicles.deviceAssign.failed'));
    } finally {
      setIsAssigning(false);
    }
  };

  const handleDeleteVehicle = async () => {
    if (!deleteConfirmVehicle) return;
    try {
      setIsDeletingVehicle(deleteConfirmVehicle._id);
      await vehiclesApi.deleteVehicle(deleteConfirmVehicle._id);
      toast.success(t('vehicles.delete.success'));
      setDeleteConfirmVehicle(null);
      await loadData();
    } catch (error: any) {
      console.error('Failed to delete vehicle:', error);
      toast.error(error.response?.data?.message || t('vehicles.delete.failed'));
    } finally {
      setIsDeletingVehicle(null);
    }
  };

  const handleUnassignDevice = async (vehicle: Vehicle) => {
    // Find the device assigned to this vehicle by matching the obd_device_id
    // The unassign endpoint requires the device's _id, but we have obd_device_id on the vehicle.
    // We pass the obd_device_id as the deviceId since the backend route uses :deviceId which
    // may accept the IMEI; adjust if the backend expects the MongoDB _id instead.
    try {
      setIsUnassigning(vehicle._id);
      await obdApi.unassignDevice(vehicle.obd_device_id);
      toast.success(t('vehicles.deviceAssign.unassigned'));
      await loadData();
    } catch (error: any) {
      console.error('Failed to unassign device:', error);
      toast.error(error.response?.data?.message || t('vehicles.deviceAssign.failedUnassign'));
    } finally {
      setIsUnassigning(null);
    }
  };

  const handleToggleSmartcar = async (vehicle: Vehicle, enabled: boolean) => {
    try {
      setTogglingSmartcar(vehicle._id);
      await vehiclesApi.toggleSmartcar(vehicle._id, enabled);
      toast.success(
        enabled
          ? t('vehicles.smartcar.enableSuccess', { plate: vehicle.plate_number })
          : t('vehicles.smartcar.disableSuccess', { plate: vehicle.plate_number })
      );
      // Optimistically update local state to avoid a full reload
      setVehicles((prev) =>
        prev.map((v) => (v._id === vehicle._id ? { ...v, smartcar_enabled: enabled } : v))
      );
    } catch (error: any) {
      console.error('Failed to toggle Smartcar:', error);
      toast.error(error.response?.data?.message || t('vehicles.smartcar.failed'));
    } finally {
      setTogglingSmartcar(null);
    }
  };

  const getDriverName = (vehicle: Vehicle): string => {
    if (!vehicle.assigned_driver) return '—';
    if (typeof vehicle.assigned_driver === 'string') {
      const driver = Array.isArray(drivers)
        ? drivers.find((d) => d._id === vehicle.assigned_driver)
        : undefined;
      return driver?.name || '—';
    }
    return vehicle.assigned_driver.name;
  };

  const getDriverId = (vehicle: Vehicle): string | undefined => {
    if (!vehicle.assigned_driver) return undefined;
    if (typeof vehicle.assigned_driver === 'string') return vehicle.assigned_driver;
    return vehicle.assigned_driver._id;
  };

  const getStatusLabel = (status: string): string => {
    if (status === 'active') return t('vehicles.table.statusActive');
    if (status === 'inactive') return t('vehicles.table.statusInactive');
    if (status === 'maintenance') return t('vehicles.table.statusMaintenance');
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Strip spaces so pasted IMEIs like "123 456 789 012 341" still match
  const filteredDevices = useMemo(() => {
    const q = deviceSearchQuery.replace(/\s/g, '').toLowerCase();
    if (!q) return availableDevices;
    return availableDevices.filter((d) => d.device_id.toLowerCase().includes(q));
  }, [availableDevices, deviceSearchQuery]);

  const handleDeviceCardKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        deviceCardRefs.current[index + 1]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (index === 0) {
          deviceSearchRef.current?.focus();
        } else {
          deviceCardRefs.current[index - 1]?.focus();
        }
      }
    },
    []
  );

  const handleDeviceSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      deviceCardRefs.current[0]?.focus();
    }
  }, []);

  const closeAssignDeviceModal = useCallback(() => {
    if (!isAssigning) {
      setIsAssignDeviceModalOpen(false);
      setDeviceSearchQuery('');
      setSelectedDeviceId('');
    }
  }, [isAssigning]);

  const filteredVehicles = Array.isArray(vehicles)
    ? vehicles.filter(
        (vehicle) =>
          vehicle.plate_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
          vehicle.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
          vehicle.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
          vehicle.obd_device_id.includes(searchQuery) ||
          getDriverName(vehicle).toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('vehicles.title')}</h1>
            <p className="text-sm text-gray-600 mt-1">{t('vehicles.subtitle')}</p>
          </div>
          <Button onClick={() => setIsAddModalOpen(true)} size="lg">
            {t('vehicles.registerVehicle')}
          </Button>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <Input
            type="search"
            placeholder={t('vehicles.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Vehicles Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
              <p className="mt-4 text-gray-600">{t('vehicles.loading')}</p>
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">🚗</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchQuery ? t('vehicles.noVehiclesFound') : t('vehicles.noVehiclesYet')}
              </h3>
              <p className="text-gray-600 mb-6">
                {searchQuery ? t('vehicles.trySearch') : t('vehicles.getStarted')}
              </p>
              {!searchQuery && (
                <Button onClick={() => setIsAddModalOpen(true)}>
                  {t('vehicles.registerFirst')}
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('vehicles.table.plateNumber')}
                    </th>
                    {isSuperAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('vehicles.table.company')}
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('vehicles.table.vehicle')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('vehicles.table.obdDevice')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('vehicles.table.deviceAction')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('vehicles.table.assignedDriver')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('vehicles.table.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('vehicles.table.odometer')}
                    </th>
                    {canToggleSmartcar && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('vehicles.table.smartcar')}
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('vehicles.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredVehicles.map((vehicle) => (
                    <tr
                      key={vehicle._id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/vehicles/${vehicle._id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {vehicle.plate_number}
                        </div>
                        {vehicle.year && (
                          <div className="text-xs text-gray-500">{vehicle.year}</div>
                        )}
                      </td>
                      {isSuperAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                            {typeof vehicle.company_id === 'object'
                              ? (vehicle.company_id as any)?.name
                              : vehicle.company_id || '—'}
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {vehicle.brand} {vehicle.model}
                        </div>
                        {vehicle.vin && (
                          <div className="text-xs text-gray-500 font-mono">{vehicle.vin}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs text-gray-500">{vehicle.obd_device_type}</div>
                        <div className="text-xs text-gray-900 font-mono">
                          {vehicle.obd_device_id}
                        </div>
                      </td>
                      {/* Device assignment action — company_admin visible column */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {vehicle.obd_device_id ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleUnassignDevice(vehicle); }}
                            disabled={isUnassigning === vehicle._id}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title={t('vehicles.deviceAssign.unassignTitle')}
                          >
                            {isUnassigning === vehicle._id ? (
                              <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                            ) : (
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                              </svg>
                            )}
                            {t('vehicles.deviceAssign.unassign')}
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); openAssignDeviceModal(vehicle); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors"
                            title={t('vehicles.deviceAssign.assignTitle')}
                          >
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                            </svg>
                            {t('vehicles.deviceAssign.assign')}
                          </button>
                        )}
                      </td>
                      {/* Conducteur column */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {vehicle.assigned_driver ? (
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {getDriverName(vehicle)}
                            </span>
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                            {t('vehicles.table.unassigned')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            vehicle.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : vehicle.status === 'maintenance'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {getStatusLabel(vehicle.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <OdometerDisplay
                          value={vehicle.current_odometer}
                          source={vehicle.odometer_source}
                          updatedAt={vehicle.odometer_updated_at}
                          compact
                        />
                      </td>
                      {canToggleSmartcar && (
                        <td
                          className="px-6 py-4 whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            role="switch"
                            aria-checked={!!vehicle.smartcar_enabled}
                            disabled={togglingSmartcar === vehicle._id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleSmartcar(vehicle, !vehicle.smartcar_enabled);
                            }}
                            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                              vehicle.smartcar_enabled ? 'bg-primary-600' : 'bg-gray-200'
                            }`}
                            title={t('vehicles.table.smartcar')}
                          >
                            {togglingSmartcar === vehicle._id ? (
                              <span className="absolute inset-0 flex items-center justify-center">
                                <svg className="animate-spin h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                              </span>
                            ) : (
                              <span
                                aria-hidden="true"
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  vehicle.smartcar_enabled ? 'translate-x-4' : 'translate-x-0'
                                }`}
                              />
                            )}
                          </button>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEditVehicle(vehicle); }}
                            className="text-primary-600 hover:text-primary-900 transition-colors"
                            title={t('vehicles.table.editTitle')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmVehicle(vehicle); }}
                            className="text-red-500 hover:text-red-700 transition-colors"
                            title={t('vehicles.delete.title')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats */}
        {!isLoading && vehicles.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('vehicles.stats.total')}</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{vehicles.length}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('vehicles.stats.active')}</div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                {Array.isArray(vehicles) ? vehicles.filter((v) => v.status === 'active').length : 0}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('vehicles.stats.assigned')}</div>
              <div className="text-2xl font-bold text-blue-600 mt-1">
                {Array.isArray(vehicles) ? vehicles.filter((v) => v.assigned_driver).length : 0}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-sm text-gray-600">{t('vehicles.stats.unassigned')}</div>
              <div className="text-2xl font-bold text-gray-600 mt-1">
                {Array.isArray(vehicles) ? vehicles.filter((v) => !v.assigned_driver).length : 0}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Vehicle Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => !isSubmitting && setIsAddModalOpen(false)}
        title={t('vehicles.addModal.title')}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Vehicle Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('vehicles.addModal.vehicleInfo')}
            </h3>
            <div className="space-y-4">
              <Input
                label={t('vehicles.addModal.plateNumber')}
                placeholder={t('vehicles.addModal.platePlaceholder')}
                value={formData.plate_number}
                onChange={(e) => handleInputChange('plate_number', e.target.value.toUpperCase())}
                error={formErrors.plate_number}
                disabled={isSubmitting}
                autoFocus
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label={t('vehicles.addModal.brand')}
                  placeholder={t('vehicles.addModal.brandPlaceholder')}
                  value={formData.brand}
                  onChange={(e) => handleInputChange('brand', e.target.value)}
                  error={formErrors.brand}
                  disabled={isSubmitting}
                />
                <Input
                  label={t('vehicles.addModal.model')}
                  placeholder={t('vehicles.addModal.modelPlaceholder')}
                  value={formData.model}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  error={formErrors.model}
                  disabled={isSubmitting}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label={t('vehicles.addModal.year')}
                  type="number"
                  placeholder={t('vehicles.addModal.yearPlaceholder')}
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', e.target.value)}
                  error={formErrors.year}
                  disabled={isSubmitting}
                />
                <Input
                  label={t('vehicles.addModal.odometer')}
                  type="number"
                  placeholder="0"
                  value={formData.current_odometer}
                  onChange={(e) => handleInputChange('current_odometer', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <Input
                label={t('vehicles.addModal.vin')}
                placeholder={t('vehicles.addModal.vinPlaceholder')}
                value={formData.vin}
                onChange={(e) => handleInputChange('vin', e.target.value.toUpperCase())}
                error={formErrors.vin}
                helperText={t('vehicles.addModal.vinHelper')}
                disabled={isSubmitting}
                maxLength={17}
              />
            </div>
          </div>

          {/* Assign Driver */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('vehicles.addModal.assignDriver')}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('vehicles.addModal.driver')}
              </label>
              <select
                value={formData.assigned_driver}
                onChange={(e) => handleInputChange('assigned_driver', e.target.value)}
                disabled={isSubmitting}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">{t('vehicles.addModal.selectDriver')}</option>
                {drivers.map((driver) => (
                  <option key={driver._id} value={driver._id}>
                    {driver.name} ({driver.email})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">{t('vehicles.addModal.driverHelper')}</p>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsAddModalOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t('vehicles.addModal.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting} className="flex-1">
              {isSubmitting
                ? t('vehicles.addModal.registering')
                : t('vehicles.addModal.registerVehicle')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Vehicle Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !isSubmitting && setIsEditModalOpen(false)}
        title={t('vehicles.editModal.title')}
        size="lg"
      >
        <form onSubmit={handleEditSubmit} className="space-y-6">
          {/* Vehicle Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('vehicles.editModal.vehicleInfo')}
            </h3>
            <div className="space-y-4">
              <Input
                label={t('vehicles.editModal.plateNumber')}
                placeholder={t('vehicles.editModal.platePlaceholder')}
                value={formData.plate_number}
                onChange={(e) => handleInputChange('plate_number', e.target.value.toUpperCase())}
                error={formErrors.plate_number}
                disabled={isSubmitting}
                autoFocus
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label={t('vehicles.editModal.brand')}
                  placeholder={t('vehicles.editModal.brandPlaceholder')}
                  value={formData.brand}
                  onChange={(e) => handleInputChange('brand', e.target.value)}
                  error={formErrors.brand}
                  disabled={isSubmitting}
                />
                <Input
                  label={t('vehicles.editModal.model')}
                  placeholder={t('vehicles.editModal.modelPlaceholder')}
                  value={formData.model}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  error={formErrors.model}
                  disabled={isSubmitting}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label={t('vehicles.editModal.year')}
                  type="number"
                  placeholder={t('vehicles.editModal.yearPlaceholder')}
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', e.target.value)}
                  error={formErrors.year}
                  disabled={isSubmitting}
                />
                <Input
                  label={t('vehicles.editModal.odometer')}
                  type="number"
                  placeholder="0"
                  value={formData.current_odometer}
                  onChange={(e) => handleInputChange('current_odometer', e.target.value)}
                  disabled={isSubmitting}
                />
                {isSuperAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Source kilométrage
                    </label>
                    <select
                      value={formData.odometer_source}
                      onChange={(e) => handleInputChange('odometer_source', e.target.value)}
                      disabled={isSubmitting}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="obd">OBD (automatique)</option>
                      <option value="manual">Manuel (accumulation)</option>
                      <option value="smartcar">Smartcar</option>
                      <option value="gps_estimate">Estimation GPS</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-400">
                      "Manuel" accumule la distance de chaque trajet au lieu d'utiliser la valeur absolue OBD.
                    </p>
                  </div>
                )}
              </div>
              <Input
                label={t('vehicles.editModal.vin')}
                placeholder={t('vehicles.editModal.vinPlaceholder')}
                value={formData.vin}
                onChange={(e) => handleInputChange('vin', e.target.value.toUpperCase())}
                error={formErrors.vin}
                helperText={t('vehicles.editModal.vinHelper')}
                disabled={isSubmitting}
                maxLength={17}
              />
            </div>
          </div>

          {/* Status */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('vehicles.editModal.status')}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('vehicles.editModal.vehicleStatus')}
              </label>
              <select
                value={editingVehicle?.status || 'active'}
                onChange={(e) => {
                  if (editingVehicle) {
                    setEditingVehicle({
                      ...editingVehicle,
                      status: e.target.value as 'active' | 'inactive' | 'maintenance',
                    });
                  }
                }}
                disabled={isSubmitting}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="active">{t('vehicles.editModal.statusActive')}</option>
                <option value="inactive">{t('vehicles.editModal.statusInactive')}</option>
                <option value="maintenance">{t('vehicles.editModal.statusMaintenance')}</option>
              </select>
            </div>
          </div>

          {/* Assign Driver */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('vehicles.editModal.assignDriver')}
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('vehicles.editModal.driver')}
              </label>
              <select
                value={formData.assigned_driver}
                onChange={(e) => handleInputChange('assigned_driver', e.target.value)}
                disabled={isSubmitting}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">{t('vehicles.editModal.noDriver')}</option>
                {drivers.map((driver) => (
                  <option key={driver._id} value={driver._id}>
                    {driver.name} ({driver.email})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsEditModalOpen(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t('vehicles.editModal.cancel')}
            </Button>
            <Button type="submit" isLoading={isSubmitting} className="flex-1">
              {isSubmitting
                ? t('vehicles.editModal.updating')
                : t('vehicles.editModal.updateVehicle')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Assign Device Modal — custom overlay for fixed header/search/footer layout */}
      {isAssignDeviceModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeAssignDeviceModal();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeAssignDeviceModal();
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">

            {/* Fixed header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {t('vehicles.deviceAssign.modalTitle')}
                </h2>
                {assigningVehicle && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    {assigningVehicle.plate_number}
                    {' '}
                    <span className="text-gray-400">
                      — {assigningVehicle.brand} {assigningVehicle.model}
                    </span>
                  </p>
                )}
              </div>
              <button
                onClick={closeAssignDeviceModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Fixed search bar */}
            {!isLoadingDevices && availableDevices.length > 0 && (
              <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    ref={deviceSearchRef}
                    type="text"
                    autoFocus
                    value={deviceSearchQuery}
                    onChange={(e) => setDeviceSearchQuery(e.target.value)}
                    onKeyDown={handleDeviceSearchKeyDown}
                    placeholder={t('vehicles.deviceAssign.searchPlaceholder')}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <p className="mt-1.5 text-xs text-gray-400">
                  {t('vehicles.deviceAssign.countAvailable', {
                    filtered: filteredDevices.length,
                    total: availableDevices.length,
                  })}
                </p>
              </div>
            )}

            {/* Scrollable device list */}
            <div className="flex-1 overflow-y-auto px-6 py-3 min-h-0">
              {isLoadingDevices ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                  <span className="ml-2 text-sm text-gray-500">{t('vehicles.deviceAssign.loadingDevices')}</span>
                </div>
              ) : availableDevices.length === 0 ? (
                <div className="text-center py-10">
                  <div className="mx-auto mb-3 flex items-center justify-center h-10 w-10 rounded-full bg-gray-100">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500">{t('vehicles.deviceAssign.noDevicesAvailable')}</p>
                </div>
              ) : filteredDevices.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-sm text-gray-500 mb-3">{t('vehicles.deviceAssign.noResults')}</p>
                  <button
                    type="button"
                    onClick={() => setDeviceSearchQuery('')}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {t('vehicles.deviceAssign.clearSearch')}
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-0.5">
                    {filteredDevices.map((device, index) => {
                      const isSelected = selectedDeviceId === device._id;
                      return (
                        <button
                          key={device._id}
                          ref={(el) => { deviceCardRefs.current[index] = el; }}
                          type="button"
                          onClick={() => setSelectedDeviceId(device._id)}
                          onKeyDown={(e) => handleDeviceCardKeyDown(e, index)}
                          className={`w-full text-left flex items-center justify-between px-4 py-3 rounded-lg transition-colors duration-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                            isSelected
                              ? 'border-2 border-blue-600 bg-blue-50'
                              : 'border border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="font-mono font-semibold text-gray-900 text-base tracking-wide">
                              {formatImeiSuffix(device.device_id)}
                            </div>
                            <div className="font-mono text-xs text-gray-400 mt-0.5">
                              {formatImei(device.device_id)}
                            </div>
                          </div>
                          {/* Selection indicator */}
                          <div className="flex-shrink-0 ml-4">
                            {isSelected ? (
                              <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {/* Scroll shadow — shown when list might overflow */}
                  {filteredDevices.length > 4 && (
                    <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                  )}
                </div>
              )}
            </div>

            {/* Fixed footer */}
            <div className="flex gap-3 px-6 py-4 border-t border-gray-200 flex-shrink-0">
              <Button
                type="button"
                variant="secondary"
                onClick={closeAssignDeviceModal}
                disabled={isAssigning}
                className="flex-1"
              >
                {t('vehicles.deviceAssign.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleAssignDevice}
                isLoading={isAssigning}
                disabled={!selectedDeviceId || isLoadingDevices || availableDevices.length === 0}
                className="flex-1 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {isAssigning
                  ? t('vehicles.deviceAssign.assigning')
                  : selectedDeviceId
                  ? t('vehicles.deviceAssign.assignWithId', {
                      suffix: formatImeiSuffix(
                        availableDevices.find((d) => d._id === selectedDeviceId)?.device_id ?? ''
                      ),
                    })
                  : t('vehicles.deviceAssign.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Vehicle Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmVehicle !== null}
        onClose={() => { if (!isDeletingVehicle) setDeleteConfirmVehicle(null); }}
        title={t('vehicles.delete.modalTitle')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('vehicles.delete.confirmMessage')}
          </p>
          {deleteConfirmVehicle && (
            <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm">
              <span className="font-semibold text-gray-900">{deleteConfirmVehicle.plate_number}</span>
              <span className="text-gray-500 ml-2">{deleteConfirmVehicle.brand} {deleteConfirmVehicle.model}</span>
            </div>
          )}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirmVehicle(null)}
              disabled={isDeletingVehicle !== null}
            >
              {t('vehicles.delete.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteVehicle}
              isLoading={isDeletingVehicle !== null}
              disabled={isDeletingVehicle !== null}
            >
              {t('vehicles.delete.confirm')}
            </Button>
          </div>
        </div>
      </Modal>

    </Layout>
  );
};
